export const EMPTY = 0;
export const RED = 1;
export const BLUE = 2;
export const MAX_LEVEL = 6;

export const DIRECTIONS = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export function opponent(player) {
  return player === RED ? BLUE : RED;
}

export function playerName(player) {
  if (player === RED) return "Red";
  if (player === BLUE) return "Blue";
  return "Draw";
}

export function generateBoard(sideLength) {
  const radius = Math.max(1, sideLength - 1);
  const cells = [];
  const lookup = new Map();

  for (let q = -radius; q <= radius; q += 1) {
    const rMin = Math.max(-radius, -q - radius);
    const rMax = Math.min(radius, -q + radius);
    for (let r = rMin; r <= rMax; r += 1) {
      const index = cells.length;
      const s = -q - r;
      const cell = { index, q, r, s, neighbors: [] };
      cells.push(cell);
      lookup.set(`${q},${r}`, index);
    }
  }

  for (const cell of cells) {
    cell.neighbors = DIRECTIONS
      .map(([dq, dr]) => lookup.get(`${cell.q + dq},${cell.r + dr}`))
      .filter((index) => index !== undefined);
    cell.distance = Math.max(Math.abs(cell.q), Math.abs(cell.r), Math.abs(cell.s));
    cell.isEdge = cell.distance === radius;
    cell.isCorner = cell.neighbors.length === 3;
    cell.centerWeight = Math.max(0, radius - cell.distance);
  }

  return { sideLength, radius, cells };
}

export function cloneState(state) {
  return {
    owners: [...state.owners],
    levels: [...state.levels],
    currentPlayer: state.currentPlayer,
    turnsPlayed: state.turnsPlayed,
    redHadTile: state.redHadTile,
    blueHadTile: state.blueHadTile,
    gameOver: state.gameOver,
    winner: state.winner,
    lastMove: state.lastMove,
    lastChainCount: state.lastChainCount,
  };
}

export function legalMovesFor(state, player) {
  const moves = [];
  for (let index = 0; index < state.owners.length; index += 1) {
    const owner = state.owners[index];
    if (owner === EMPTY || owner === player) moves.push(index);
  }
  return moves;
}

export function totalsFor(state) {
  const totals = {
    redScore: 0,
    blueScore: 0,
    redTiles: 0,
    blueTiles: 0,
    primed: 0,
  };

  for (let index = 0; index < state.owners.length; index += 1) {
    const owner = state.owners[index];
    const level = state.levels[index];
    if (level === MAX_LEVEL - 1) totals.primed += 1;
    if (owner === RED) {
      totals.redScore += level;
      totals.redTiles += 1;
    } else if (owner === BLUE) {
      totals.blueScore += level;
      totals.blueTiles += 1;
    }
  }
  return totals;
}

export function terminalWinner(state) {
  const { redTiles, blueTiles } = totalsFor(state);
  if (redTiles === 0 && blueTiles === 0 && state.redHadTile && state.blueHadTile) return EMPTY;
  if (redTiles === 0 && blueTiles > 0 && state.redHadTile) return BLUE;
  if (blueTiles === 0 && redTiles > 0 && state.blueHadTile) return RED;
  return null;
}

function markParticipation(state) {
  if (state.owners.includes(RED)) state.redHadTile = true;
  if (state.owners.includes(BLUE)) state.blueHadTile = true;
}

export function resolveExplosions(state, cells, startIndex, trace = true) {
  const queue = [startIndex];
  const queued = new Set(queue);
  const events = [];

  for (let head = 0; head < queue.length; head += 1) {
    const source = queue[head];
    queued.delete(source);
    const owner = state.owners[source];
    const levelBefore = state.levels[source];
    if (owner === EMPTY || levelBefore < MAX_LEVEL) continue;

    const burstCount = Math.floor(levelBefore / MAX_LEVEL);
    const remainder = levelBefore % MAX_LEVEL;
    if (remainder > 0) {
      state.owners[source] = owner;
      state.levels[source] = remainder;
    } else {
      state.owners[source] = EMPTY;
      state.levels[source] = 0;
    }

    const affected = [...cells[source].neighbors];
    for (const neighbor of affected) {
      state.owners[neighbor] = owner;
      state.levels[neighbor] += burstCount;
      if (state.levels[neighbor] >= MAX_LEVEL && !queued.has(neighbor)) {
        queued.add(neighbor);
        queue.push(neighbor);
      }
    }

    if (trace) {
      events.push({
        source,
        owner,
        levelBefore,
        burstCount,
        remainder,
        affected,
        owners: [...state.owners],
        levels: [...state.levels],
      });
    }
  }

  return events;
}

export function simulateMove(state, cells, move, player, trace = false) {
  const next = cloneState(state);
  if (next.gameOver) return { state: next, events: [], legal: false };
  const owner = next.owners[move];
  if (owner !== EMPTY && owner !== player) return { state: next, events: [], legal: false };

  next.owners[move] = player;
  next.levels[move] += 1;
  markParticipation(next);
  const placed = { owners: [...next.owners], levels: [...next.levels] };
  const events = next.levels[move] >= MAX_LEVEL
    ? resolveExplosions(next, cells, move, trace)
    : [];

  next.turnsPlayed += 1;
  next.lastMove = move;
  next.lastChainCount = events.length;
  markParticipation(next);
  const winner = terminalWinner(next);
  if (winner !== null) {
    next.gameOver = true;
    next.winner = winner;
  } else {
    next.currentPlayer = opponent(player);
  }

  return { state: next, events, placed, legal: true };
}

export class HexplodeGame {
  constructor(sideLength = 4, startingPlayer = RED) {
    this.history = [];
    this.reset(sideLength, startingPlayer);
  }

  reset(sideLength = this.board?.sideLength ?? 4, startingPlayer = RED) {
    this.board = generateBoard(sideLength);
    this.state = {
      owners: new Array(this.board.cells.length).fill(EMPTY),
      levels: new Array(this.board.cells.length).fill(0),
      currentPlayer: startingPlayer,
      turnsPlayed: 0,
      redHadTile: false,
      blueHadTile: false,
      gameOver: false,
      winner: null,
      lastMove: null,
      lastChainCount: 0,
    };
    this.history = [];
    return this.snapshot();
  }

  snapshot() {
    return cloneState(this.state);
  }

  restore(snapshot) {
    this.state = cloneState(snapshot);
    return this.snapshot();
  }

  legalMoves(player = this.state.currentPlayer) {
    return legalMovesFor(this.state, player);
  }

  isLegal(move, player = this.state.currentPlayer) {
    const owner = this.state.owners[move];
    return !this.state.gameOver && (owner === EMPTY || owner === player);
  }

  applyMove(move) {
    const player = this.state.currentPlayer;
    const before = this.snapshot();
    const result = simulateMove(this.state, this.board.cells, move, player, true);
    if (!result.legal) return { ...result, before, player, move };
    this.history.push(before);
    this.state = result.state;
    return { ...result, before, after: this.snapshot(), player, move };
  }

  undo(steps = 1) {
    let restored = null;
    for (let count = 0; count < steps && this.history.length; count += 1) {
      restored = this.history.pop();
    }
    if (!restored) return null;
    return this.restore(restored);
  }

  totals() {
    return totalsFor(this.state);
  }
}
